import React, { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'

import type { ExternalIdentifier } from 'types/writer'

import { createWriter } from '@disco/state-management/actions/rights-data-actions'
import { updateSplit } from '@disco/state-management/reducers/rightsManagement'

import { useDispatch } from 'hooks/use-redux'

import { BaseDialog } from '../../components/base-dialog'
import { useCreateRightsEntity, useDuplicateWriterSearch } from '../../hooks'
import type { RightsDataModalLocationState } from '../../types'

/**
 * CreateWriterModal - Modal for creating a new writer
 *
 * This modal allows users to create a new writer contact by providing a name
 * IPI, and ISNI.
 * Writers are contacts with the 'Writer' tag applied.
 *
 *
 * When a writer is created from a split row (via splitId and splitsKey in location state),
 * the split is automatically updated with the new writer.
 *
 * Includes duplicate name detection via debounced search to warn users
 * if a writer with the same name already exists in their contact list.
 *
 * Note on duplicate detection approach:
 * Unlike PRO and Publisher modals which use synchronous Redux state checks,
 * Writer duplicate detection uses async API search via useDuplicateWriterSearch.
 * This is because Writers can have thousands of entries per user, making it
 * impractical to load all into Redux state. The trade-off is a slight delay
 * (debounce + API latency) before showing duplicate warnings for Writers.
 */
export function CreateWriterModal() {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    searchText = '',
    splitId,
    splitsKey,
  } = (location.state as RightsDataModalLocationState) || {}

  const createEntity = useCreateRightsEntity({
    entityType: 'Writer',
    createThunk: createWriter,
  })

  const [inputValue, setInputValue] = useState(searchText)

  const { warningMessage } = useDuplicateWriterSearch({
    inputValue,
  })

  const handleNameChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  const handleSave = useCallback(
    async (
      name: string,
      externalIdentifiers: ExternalIdentifier[] | undefined,
    ) => {
      const createdWriterId = await createEntity(name, externalIdentifiers)

      // Auto-select the created writer in the split that triggered the creation
      if (splitId && splitsKey) {
        dispatch(
          updateSplit({
            trackId: splitsKey,
            splitId,
            changes: { writerId: createdWriterId },
          }),
        )
      }
    },
    [dispatch, splitId, splitsKey, createEntity],
  )

  return (
    <BaseDialog
      heading="Create new writer"
      nameFieldLabel="Writer name"
      onSave={handleSave}
      initialName={searchText}
      warningMessage={warningMessage}
      onNameChange={handleNameChange}
      showIPI
      showISNI
      open
    />
  )
}

export default CreateWriterModal
